// Security utilities for the application

// Generate a random nonce for CSP
export const generateNonce = (): string => {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return btoa(String.fromCharCode(...array));
};

// Secure session storage wrapper
export class SecureStorage {
  private static encrypt(data: string): string {
    // In production, this would use actual encryption
    // For now, we'll use base64 encoding as a placeholder
    return btoa(encodeURIComponent(data));
  }

  private static decrypt(data: string): string {
    // In production, this would use actual decryption
    try {
      return decodeURIComponent(atob(data));
    } catch {
      return '';
    }
  }

  static setItem(key: string, value: string): void {
    const encrypted = this.encrypt(value);
    localStorage.setItem(key, encrypted);
  }

  static getItem(key: string): string | null {
    const encrypted = localStorage.getItem(key);
    if (!encrypted) return null;
    return this.decrypt(encrypted);
  }

  static removeItem(key: string): void {
    localStorage.removeItem(key);
  }

  static clear(): void {
    localStorage.clear();
  }
}

// CSRF token management
export class CSRFToken {
  private static TOKEN_KEY = 'csrf_token';

  static generate(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    const token = Array.from(array, (byte) =>
      byte.toString(16).padStart(2, '0')
    ).join('');
    SecureStorage.setItem(this.TOKEN_KEY, token);
    return token;
  }

  static get(): string {
    const existing = SecureStorage.getItem(this.TOKEN_KEY);
    if (existing) return existing;
    return this.generate();
  }

  static validate(token: string): boolean {
    const stored = SecureStorage.getItem(this.TOKEN_KEY);
    return stored === token && token.length > 0;
  }

  static refresh(): string {
    return this.generate();
  }
}

// XSS prevention helpers
export const escapeHtml = (unsafe: string): string => {
  return unsafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

// URL validation
export const isValidUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url);
    return ['http:', 'https:'].includes(parsed.protocol);
  } catch {
    return false;
  }
};

// Sanitize URL for redirects
export const sanitizeRedirectUrl = (
  url: string,
  allowedDomains: string[] = []
): string => {
  try {
    const parsed = new URL(url, window.location.origin);

    // Only allow same-origin redirects by default
    if (parsed.origin === window.location.origin) {
      return parsed.pathname + parsed.search + parsed.hash;
    }

    // Check if the domain is in the allowed list
    if (allowedDomains.some((domain) => parsed.hostname === domain)) {
      return url;
    }

    // Default to home page for invalid redirects
    return '/';
  } catch {
    return '/';
  }
};

// Content Security Policy builder
export class CSPBuilder {
  private directives: Map<string, string[]> = new Map();

  constructor() {
    // Set default directives
    this.directives.set('default-src', ["'self'"]);
    this.directives.set('frame-ancestors', ["'none'"]);
    this.directives.set('base-uri', ["'self'"]);
    this.directives.set('form-action', ["'self'"]);
  }

  addDirective(directive: string, values: string[]): this {
    const existing = this.directives.get(directive) || [];
    this.directives.set(directive, [...existing, ...values]);
    return this;
  }

  build(): string {
    const parts: string[] = [];

    for (const [directive, values] of this.directives) {
      parts.push(`${directive} ${values.join(' ')}`);
    }

    return parts.join('; ');
  }
}

// Session timeout monitor
export class SessionMonitor {
  private checkInterval: NodeJS.Timeout | null = null;
  private warningShown = false;

  constructor(
    private onExpiry: () => void,
    private onWarning?: () => void,
    private checkIntervalMs = 60000, // Check every minute
    private warningThresholdMs = 5 * 60 * 1000 // Warn at 5 minutes
  ) {}

  start(expiresAt: Date): void {
    this.stop(); // Clear any existing interval

    this.checkInterval = setInterval(() => {
      const now = Date.now();
      const expiry = expiresAt.getTime();
      const timeUntilExpiry = expiry - now;

      // Show warning once when approaching expiry
      if (
        timeUntilExpiry <= this.warningThresholdMs &&
        timeUntilExpiry > 0 &&
        !this.warningShown
      ) {
        this.warningShown = true;
        this.onWarning?.();
      }

      // Handle expiry
      if (timeUntilExpiry <= 0) {
        this.stop();
        this.onExpiry();
      }
    }, this.checkIntervalMs);
  }

  stop(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    this.warningShown = false;
  }

  reset(newExpiresAt: Date): void {
    this.warningShown = false;
    this.start(newExpiresAt);
  }
}
